import { getProgramEnrollmentWindowStatus } from '@/apis/programs/requests.ts'
import { AppDispatch, RootState } from '@/app-storage/store.ts'
import {
  POSTLogin,
  ResetSessionRequest,
  setRememberMe,
} from '@/app-storage/user/authSessionSlice.ts'
import { useAppAlert } from '@/app/AppAlertProvider.tsx'
import { AUTH_MODE, keycloakAuthService } from '@/auth/keycloak'
import { Button } from '@/common/components/Button.tsx'
import { CircleLoader } from '@/common/components/CircleLoader.tsx'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import { Input } from '@/common/components/Input.tsx'
import { PLACEHOLDER_EMAIL } from '@/common/constants.ts'
import { EyeIcon, EyeSlashIcon } from '@/common/icons/general'
import { REQUEST_STATUS } from '@/common/types_and_interfaces.ts'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { bootstrapFromKeycloak } from '@/storage/user/authSessionSlice'
import AceScholarshipsLogo from '@assets/AceScholarshipLogo1.jpg'
import { zodResolver } from '@hookform/resolvers/zod'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { AuthLayout } from '../layout.tsx'

// const RenewalSimFormSchema = z.object({
//   email: z
//     .string({ message: 'Please enter a valid email address' })
//     .email({ message: 'Please enter a valid email address' }),
// })
// const V1ApplicantRenewalSimulation = () => {
//   const NavigateTo = useNavigate()
//   const [dialogOpen, setDialogOpen] = useState(false)
//   const [errorMessage, setErrorMessage] = useState('')
//   const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
//     event.preventDefault()
//     const formData = new FormData(event.currentTarget)
//     const formValues = Object.fromEntries(formData.entries())
//     console.log(formValues)
//     const result = RenewalSimFormSchema.safeParse(formValues)
//
//     if (result.success) {
//       const uri = `external_applicant_id=${encodeURIComponent('fake-' + result.data.email)}&simulate=true`
//       NavigateTo(AppRoutePaths.renewal_bridge.index + '?' + uri)
//     } else {
//       console.log(result.error.message)
//       const firstError = result.error.errors[0]
//       setErrorMessage(firstError.message)
//     }
//   }
//   return (
//     <>
//       <Button
//         className="my-4 w-36"
//         variant="dev_feature"
//         onClick={() => setDialogOpen(!dialogOpen)}
//       >
//         Simulate Renewal
//       </Button>
//       <AppDialog open={dialogOpen} setOpen={setDialogOpen}>
//         <h2 className="text-lg font-semibold pb-3">
//           Run A v1 Applicant Renewal Simulation
//         </h2>
//         <form onSubmit={handleSubmit}>
//           <label>
//             <span className="font-medium text-gray-600">Email</span>
//             <Input name="email" type="text" />
//           </label>
//           {errorMessage != '' && (
//             <span className="text-red-500 pt-2">{errorMessage}</span>
//           )}
//           <Button className="w-full mt-6" type="submit">
//             Start
//           </Button>
//         </form>
//       </AppDialog>
//     </>
//   )
// }

export default function LoginPage() {
  const { t: translate } = useTranslation()
  const [enrollmentOpen, setEnrollmentOpen] = useState(false)
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()

  // const onDevEnv = AppEnv('development') || String(ENV.VITE_API_URL).includes('dev')
  // const onSandboxEnv = String(ENV.VITE_API_URL).includes('sandbox')

  const checkedKeycloack = useRef(false)
  useEffect(() => {
    if (AUTH_MODE !== 'keycloak') return
    if (!checkedKeycloack.current) {
      if (keycloakAuthService().isAuthenticated()) {
        navigateNext()
      } else {
        keycloakAuthService().login()
      }
      checkedKeycloack.current = true
    }
  }, [])

  const navigateNext = async () => {
    await dispatch(bootstrapFromKeycloak())
    navigate(AppRoutePaths.dashboard.index)
  }

  useEffect(() => {
    getProgramEnrollmentWindowStatus()
      .then((response) => {
        if (response.data) {
          setEnrollmentOpen(response.data.general_enrollment_open)
        }
      })
      .catch() // This function will never throw an exception
  }, [])

  if (AUTH_MODE === 'keycloak') {
    return (
      <AuthLayout>
        <AuthLayout.Content className="space-y-14">
          <div className="text-center text-sm text-gray-500 py-12">
            {translate('authenticating')}
          </div>
        </AuthLayout.Content>
      </AuthLayout>
    )
  }
  return (
    <>
      {/*{(onDevEnv || onSandboxEnv) && (*/}
      {/*  <div className="flex justify-end px-4">*/}
      {/*    <V1ApplicantRenewalSimulation />*/}
      {/*  </div>*/}
      {/*)}*/}
      <AuthLayout>
        <AuthLayout.Content>
          <LoginForm />
        </AuthLayout.Content>
        <div className="mt-10 text-center text-sm text-gray-500">
          {enrollmentOpen && (
            <div>
              {translate('dont_have_account')}{' '}
              <Link
                className="text-primary font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-primary"
                to={AppRoutePaths.auth.register}
              >
                {translate('sign_up')}
              </Link>
            </div>
          )}

          {/*<div className="mt-5">*/}
          {/*  {translate('need_to_access_2024_25')}{' '}*/}
          {/*  <Link*/}
          {/*    className="text-primary font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-primary"*/}
          {/*    to="https://access.ufascholarship.com/api/auth/login"*/}
          {/*    target="_blank"*/}
          {/*  >*/}
          {/*    {translate('click_here')}*/}
          {/*  </Link>*/}
          {/*</div>*/}
        </div>
      </AuthLayout>
    </>
  )
}

const UserLoginFormSchema = z.object({
  email: z.string().email({ message: 'please_enter_a_valid_email_address' }),
  password: z
    .string()
    .min(8, { message: 'password_must_be_at_least_characters_long' }),
  remember_me: z.boolean(),
})
type UserLoginForm = z.infer<typeof UserLoginFormSchema>

const LoginForm = () => {
  const { t: translate } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)
  const navigate = useNavigate()
  const asyncDispatch = useDispatch<AppDispatch>()
  const AuthState = useSelector((state: RootState) => state.AuthSession)
  const AuthRequestState = AuthState.request
  const { fireAlert } = useAppAlert()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UserLoginForm>({
    resolver: zodResolver(UserLoginFormSchema),
  })
  const onSubmit = (userForm: UserLoginForm) => {
    asyncDispatch(POSTLogin(userForm))
  }

  const rememberMeHandled = (event: ChangeEvent<HTMLInputElement>) => {
    asyncDispatch(setRememberMe(event.target.checked))
  }

  useEffect(() => {
    asyncDispatch(setRememberMe(false))
  }, [])

  useEffect(() => {
    if (AuthRequestState.status === REQUEST_STATUS.FAILED) {
      fireAlert({
        variant: 'error',
        message: AuthRequestState.error.message,
      }).then()
      asyncDispatch(ResetSessionRequest())
      return
    }
    if (AuthRequestState.status === REQUEST_STATUS.SUCCESS) {
      if (AuthState.require2FA) {
        //TODO: Uncomment the below line and remove GetScholarship()
        navigate(AppRoutePaths.auth.twofa)
      } else {
        navigate(AppRoutePaths.dashboard.index)
      }
      asyncDispatch(ResetSessionRequest())
      return
    }
  }, [AuthRequestState.status, AuthState.require2FA])

  return (
    <div>
      <img
        alt={translate('ace_logo')}
        src={AceScholarshipsLogo}
        className="mx-auto h-auto"
        width="100%"
      />
      <h1
        data-testid="login-heading"
        className="mt-3 text-center text-2xl font-bold text-gray-900"
      >
        {translate('welcome_back')}
      </h1>
      <form
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-5 mt-7"
      >
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            {translate('email')}
          </label>
          <div className="mt-1">
            <Input
              id="email"
              {...register('email')}
              type="email"
              required
              autoComplete="email"
              placeholder={PLACEHOLDER_EMAIL}
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            <FormErrorMessage
              message={translate(errors.email?.message ?? '')}
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            {translate('password')}
          </label>
          <div className="mt-1 relative">
            <Input
              id="password"
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="current-password"
              placeholder={translate('enter_your_password')}
              aria-invalid={errors.password ? 'true' : 'false'}
              aria-describedby={errors.password ? 'password-error' : undefined}
            />
            <button
              type="button"
              className="absolute right-0 top-0 flex items-center h-full mr-2 focus:outline-none focus:ring-2 focus:ring-primary"
              onClick={() => {
                setShowPassword(!showPassword)
              }}
              aria-label={
                showPassword
                  ? translate('hide_password')
                  : translate('show_password')
              }
            >
              {showPassword ? (
                <EyeIcon aria-hidden="true" />
              ) : (
                <EyeSlashIcon aria-hidden="true" />
              )}
            </button>
            <FormErrorMessage
              message={translate(errors.password?.message ?? '')}
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              {...register('remember_me')}
              id="remember_me"
              name="remember_me"
              type="checkbox"
              onChange={rememberMeHandled}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
            />
            <label
              htmlFor="remember_me"
              className="ml-3 block text-sm leading-6 text-gray-900"
            >
              {translate('remember_me')}
            </label>
          </div>

          <Link
            to={AppRoutePaths.auth.forgot_password.enter_email}
            className="text-sm font-semibold text-primary-600 hover:text-primary-500 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {translate('forgot_password')}
          </Link>
        </div>

        <div className="flex">
          <Button type="submit" className="grow">
            {AuthRequestState.status == REQUEST_STATUS.IN_FLIGHT && (
              <CircleLoader size="sm" className="mr-1" aria-hidden="true" />
            )}
            <span>
              {AuthRequestState.status == REQUEST_STATUS.IN_FLIGHT
                ? `${translate('authenticating')}...`
                : translate('log_in')}
            </span>
          </Button>
        </div>
      </form>
    </div>
  )
}
